<script setup lang="ts">
import AppContextPanel from '@/components/app-shell/AppContextPanel.vue'
import CompanyCreateDialog from '@/components/companies/CompanyCreateDialog.vue'
import CompanyOrganizationChart from '@/components/companies/CompanyOrganizationChart.vue'
import FinestAssistantPanel from '@/components/FinestAssistantPanel.vue'
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import EmptyState from '@/components/EmptyState.vue'
import CompaniesController from '@controllers/Investors/CompaniesController'
import ShareholderController from '@controllers/Investors/CompanyShareholderController'
import { Head, router } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CompanyData = App.Data.Company.CompanyData
import type { OrgGraph } from '@/components/companies/orgGraph'

const props = defineProps<{
  companies: CompanyData[]
  companyGraph: OrgGraph
  focusNodeId: string | null
  companiesCompletion: number
}>()

const { t } = useI18n()

const companyById = computed(() => new Map(props.companies.map((company) => [company.id as string, company])))

const nodeNameById = computed(() => new Map(props.companyGraph.nodes.map((node) => [node.id, node.name])))

const editCompany = (id: string) => {
  router.visit(CompaniesController.show(id).url)
}

type ConfirmConfig = {
  header: string
  message: string
  acceptLabel: string
  rejectLabel: string
  action: () => void
}

const confirmVisible = ref(false)
const confirmConfig = ref<ConfirmConfig | null>(null)

const requestConfirm = (config: ConfirmConfig) => {
  confirmConfig.value = config
  confirmVisible.value = true
}

const acceptConfirm = () => {
  confirmConfig.value?.action()
  confirmVisible.value = false
}

const deleteCompany = (id: string) => {
  const company = companyById.value.get(id)
  if (!company) {
    return
  }

  requestConfirm({
    header: t('deleteConfirm.header'),
    message: t('deleteConfirm.message', { name: company.name }),
    acceptLabel: t('deleteConfirm.accept'),
    rejectLabel: t('deleteConfirm.reject'),
    action: () =>
      router.delete(CompaniesController.destroy(id).url, {
        preserveScroll: true,
        onSuccess: () => router.reload({ only: ['companies', 'companyGraph', 'companiesCompletion'] }),
      }),
  })
}

const deletePerson = (id: string) => {
  requestConfirm({
    header: t('deletePersonConfirm.header'),
    message: t('deletePersonConfirm.message', { name: nodeNameById.value.get(id) ?? '' }),
    acceptLabel: t('deletePersonConfirm.accept'),
    rejectLabel: t('deletePersonConfirm.reject'),
    action: () =>
      router.delete(ShareholderController.destroy(id).url, {
        preserveScroll: true,
        onSuccess: () => router.reload({ only: ['companies', 'companyGraph', 'companiesCompletion'] }),
      }),
  })
}
</script>

<template>
  <Head :title="t('heading')" />

  <AppContextPanel v-if="companies.length">
    <FinestAssistantPanel :completion="companiesCompletion" context="companies" :show-documents="true" />
  </AppContextPanel>

  <Dialog v-model:visible="confirmVisible" modal :header="confirmConfig?.header" class="w-full max-w-md">
    <div class="flex items-start gap-3">
      <i class="pi pi-exclamation-triangle text-danger mt-0.5 text-xl" aria-hidden="true" />
      <p class="text-ink-2">{{ confirmConfig?.message }}</p>
    </div>

    <template #footer>
      <FinestButton :label="confirmConfig?.rejectLabel" variant="ghost" @click="confirmVisible = false" />
      <FinestButton :label="confirmConfig?.acceptLabel" variant="danger" @click="acceptConfirm" />
    </template>
  </Dialog>

  <div class="flex flex-col lg:h-[calc(100dvh-4rem)]">
    <div class="mb-4">
      <p class="section-label">{{ t('eyebrow') }}</p>
      <div class="flex items-center gap-2">
        <h1 class="page-title">{{ t('heading') }}</h1>
        <PageTitleHelp :text="t('subtitle')" />
      </div>
    </div>

    <div v-if="companyGraph.nodes.length" class="graph-card-wrap min-h-[24rem] flex-1 lg:min-h-0">
      <FinestCard :title="t('graphCard.title')" icon="pi pi-sitemap" flush>
        <template #actions>
          <CompanyCreateDialog />
        </template>

        <CompanyOrganizationChart
          :graph="companyGraph"
          :interactive="true"
          :focus-node-id="focusNodeId"
          fill
          @edit="editCompany"
          @delete="deleteCompany"
          @delete-person="deletePerson" />
      </FinestCard>
    </div>

    <EmptyState v-else icon="pi pi-building" :description="t('emptyState.description')" :title="t('emptyState.title')">
      <template #action>
        <CompanyCreateDialog />
      </template>
    </EmptyState>
  </div>
</template>

<style scoped>
.graph-card-wrap {
  display: flex;
  flex-direction: column;
}

.graph-card-wrap :deep(.p-card) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.graph-card-wrap :deep(.p-card-body) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.graph-card-wrap :deep(.p-card-content) {
  flex: 1 1 auto;
  min-height: 0;
}

.graph-card-wrap :deep(.p-card-content) > div,
.graph-card-wrap :deep(.p-card-content) > div > div {
  height: 100%;
}
</style>

<i18n>
{
  "de": {
    "eyebrow": "Meine Basis",
    "heading": "Unternehmen",
    "subtitle": "Deine Gesellschaftsstruktur an einem Ort – fahre über eine Node, um eine Gesellschaft zu bearbeiten oder zu löschen.",
    "graphCard": {
      "title": "Beteiligungsstruktur"
    },
    "deleteConfirm": {
      "header": "Unternehmen löschen",
      "message": "Möchtest du „{name}“ wirklich löschen?",
      "accept": "Löschen",
      "reject": "Abbrechen"
    },
    "deletePersonConfirm": {
      "header": "Person löschen",
      "message": "Möchtest du „{name}“ wirklich löschen? Die Person wird endgültig gelöscht und als Gesellschafter aus allen Unternehmen entfernt.",
      "accept": "Löschen",
      "reject": "Abbrechen"
    },
    "emptyState": {
      "title": "Keine Unternehmen",
      "description": "Du hast noch keine Unternehmen hinzugefügt"
    }
  },
  "en": {
    "eyebrow": "My Base",
    "heading": "Companies",
    "subtitle": "Your company structure in one place – hover a node to edit or delete a company.",
    "graphCard": {
      "title": "Ownership structure"
    },
    "deleteConfirm": {
      "header": "Delete company",
      "message": "Do you really want to delete “{name}”?",
      "accept": "Delete",
      "reject": "Cancel"
    },
    "deletePersonConfirm": {
      "header": "Delete person",
      "message": "Do you really want to delete “{name}”? The person will be permanently deleted and removed as a shareholder from all companies.",
      "accept": "Delete",
      "reject": "Cancel"
    },
    "emptyState": {
      "title": "No companies",
      "description": "You have not added any companies yet"
    }
  }
}
</i18n>
